import { useState, useCallback } from 'react';
import { DrawingState, DrawingTool, Rectangle } from '../types';

export const useDrawing = () => {
  const [drawingState, setDrawingState] = useState<DrawingState>({
    activeTool: 'pan',
    isDrawing: false,
    startPoint: null,
    rectangles: {},
    selectedRectangle: null
  });

  const setActiveTool = useCallback((tool: DrawingTool) => {
    setDrawingState(prev => ({
      ...prev,
      activeTool: tool,
      isDrawing: false,
      startPoint: null
    }));
  }, []);

  const startDrawing = useCallback((imageIndex: number, x: number, y: number) => {
    if (drawingState.activeTool === 'rectangle') {
      setDrawingState(prev => ({
        ...prev,
        isDrawing: true,
        startPoint: { x, y }
      }));
    }
  }, [drawingState.activeTool]);

  const updateDrawing = useCallback((x: number, y: number) => {
    if (drawingState.isDrawing && drawingState.startPoint) {
      // Drawing preview handled in component
    }
  }, [drawingState.isDrawing, drawingState.startPoint]);

  const finishDrawing = useCallback((imageIndex: number, x: number, y: number, onRectangleChange?: (rectangles: Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>>) => void) => {
    console.log('=== FINISH DRAWING DEBUG ===');
    console.log('Image index:', imageIndex);
    console.log('Coordinates:', x, y);
    console.log('Drawing state:', drawingState);
    
    if (drawingState.isDrawing && drawingState.startPoint && drawingState.activeTool === 'rectangle') {
      const startX = Math.min(drawingState.startPoint.x, x);
      const startY = Math.min(drawingState.startPoint.y, y);
      const width = Math.abs(x - drawingState.startPoint.x);
      const height = Math.abs(y - drawingState.startPoint.y);

      // Only create rectangle if it has some size
      if (width > 5 && height > 5) {
        const newRectangle: Rectangle = {
          x: startX,
          y: startY,
          width,
          height,
          color: '#84cc16'
        };

        console.log('=== CREATING NEW RECTANGLE ===');
        console.log('New rectangle:', newRectangle);
        console.log('For image index:', imageIndex);

        setDrawingState(prev => {
          const newRectangles = {
            ...prev.rectangles,
            [imageIndex]: [newRectangle] // Single rectangle per image
          };
          
          console.log('=== UPDATED RECTANGLES STATE ===');
          console.log('Previous rectangles:', prev.rectangles);
          console.log('New rectangles:', newRectangles);
          
          // Convert to string keys for callback if provided
          if (onRectangleChange) {
            const stringKeyRectangles: Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>> = {};
            Object.entries(newRectangles).forEach(([key, rects]) => {
              const numKey = parseInt(key);
              const stringKey = numKey === 0 ? 'primary1' : 'secondary1';
              stringKeyRectangles[stringKey] = rects;
            });
            console.log('=== CALLING RECTANGLE CHANGE CALLBACK ===');
            console.log('String key rectangles:', stringKeyRectangles);
            onRectangleChange(stringKeyRectangles);
          }
          
          return {
            ...prev,
            isDrawing: false,
            startPoint: null,
            rectangles: newRectangles
          };
        });
      } else {
        setDrawingState(prev => ({
          ...prev,
          isDrawing: false,
          startPoint: null
        }));
      }
    }
  }, [drawingState]);

  const clearRectangles = useCallback((imageKey: number | string, onRectangleChange?: (rectangles: Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>>) => void) => {
    // Use setTimeout to avoid React 18 batching issues that can cause flickering
    setTimeout(() => {
      setDrawingState(prev => {
        const newRectangles = {
          ...prev.rectangles,
          [imageKey]: []
        };

        // Call the onRectangleChange callback to sync state up
        if (onRectangleChange) {
          console.log('=== CALLING ON RECTANGLE CHANGE AFTER CLEAR ===');
          console.log('Updated rectangles being sent:', newRectangles);
          onRectangleChange(newRectangles);
        }

        return {
          ...prev,
          rectangles: newRectangles,
          selectedRectangle: null
        };
      });
    }, 0);
  }, []);

  const clearAllRectangles = useCallback(() => {
    // Use setTimeout to avoid React 18 batching issues that can cause flickering
    setTimeout(() => {
      setDrawingState(prev => ({
        ...prev,
        rectangles: {},
        selectedRectangle: null
      }));
    }, 0);
  }, []);

  const selectRectangle = useCallback((imageId: string, rectIndex: number) => {
    console.log('=== SELECT RECTANGLE CALLED ===');
    console.log('Image ID:', imageId, 'Rect Index:', rectIndex);
    setDrawingState(prev => {
      console.log('Previous selected:', prev.selectedRectangle);
      const newSelected = { imageId, rectIndex };
      console.log('New selected:', newSelected);
      return {
        ...prev,
        selectedRectangle: newSelected
      };
    });
  }, []);

  const deleteSelectedRectangle = useCallback((onRectangleChange?: (rectangles: Record<string, Array<{ x: number; y: number; width: number; height: number; color?: string }>>) => void) => {
    console.log('=== DELETE SELECTED RECTANGLE CALLED ===');
    setDrawingState(prev => {
      console.log('Current selected rectangle:', prev.selectedRectangle);
      console.log('Current rectangles:', prev.rectangles);
      
      if (!prev.selectedRectangle) {
        console.log('No rectangle selected for deletion');
        return prev;
      }
      
      const { imageId, rectIndex } = prev.selectedRectangle;
      console.log('Deleting rectangle at imageId:', imageId, 'rectIndex:', rectIndex);
      
      const currentRects = prev.rectangles[imageId] || [];
      console.log('Current rectangles for image:', currentRects);
      
      const newRects = currentRects.filter((_, index) => index !== rectIndex);
      console.log('Rectangles after deletion:', newRects);
      
      const newRectangles = {
        ...prev.rectangles,
        [imageId]: newRects
      };
      
      // Call the onRectangleChange callback to sync state up
      if (onRectangleChange) {
        console.log('=== CALLING ON RECTANGLE CHANGE AFTER DELETE ===');
        console.log('Updated rectangles being sent:', newRectangles);
        onRectangleChange(newRectangles);
      }
      
      const newState = {
        ...prev,
        rectangles: newRectangles,
        selectedRectangle: null
      };
      
      console.log('New drawing state:', newState);
      return newState;
    });
  }, []);

  const setRectangles = useCallback((rectangles: Record<number | string, Rectangle[]>) => {
    setDrawingState(prev => ({
      ...prev,
      rectangles
    }));
  }, []);

  return {
    drawingState,
    setActiveTool,
    startDrawing,
    updateDrawing,
    finishDrawing,
    clearRectangles,
    clearAllRectangles,
    setRectangles,
    selectRectangle,
    deleteSelectedRectangle
  };
};